import {
  Autocomplete,
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomPagination } from '../../../../../../shared-components/pagination/CustomPagination'
import { CustomTextfield } from '../../../../../../shared-components/textfield/CustomTextField'
import styles from './Statistics.module.scss'
import instance from '../../../../../../../providers/axios.config'
import Notiflix from 'notiflix'
import Skeleton from '@mui/material/Skeleton'
import { formatDate } from '../../../../../../../utils/helpers'

type headerStatisticsType = {
  total_downloads?: number,
  total_earnings?: string,
  total_unpaid_earnings?: string,
}
type statisticType = {
  downloads?: number,
  earnings?: string,
  id?: number,
  thumbURL?: string,
  upload_date?: Date
}

export const Statistics = () => {
  const { t } = useTranslation()

  const [date, setDate] = useState()
  const [selectedValueMonth, setSelectedValueMonth] = useState('all')
  const [imageIds, setImageIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [statistics, setStatistics] = useState<statisticType[]>([])
  const [headerStatistics, setHeaderStatistics] = useState<headerStatisticsType>({})
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    totalPages: 1
  })

  const handleChange = (event: any, value: any) => {
    setPagination({ ...pagination, currentPage: value })
  }

  const getMyStatistics = async () => {
    setIsLoading(true)
    try {
      const constructUrlDate = date ? `&filters[date]=${moment(date).format('MM.YYYY')}` : ''
      let params = `limit=${pagination.perPage}&page=${pagination.currentPage}`
      
      if (constructUrlDate) {
        params += `${constructUrlDate}`
      }
      
      if (selectedValueMonth && selectedValueMonth !== 'all') {
        params += `&filters[months]=${selectedValueMonth}`
      }
      
      // Add image IDs if any exist
      if (imageIds.length > 0) {
        params += `&filters[images]=${imageIds.join(',')}`
      }
      
      const res = await instance.get(`seller/statistics?${params}`)
      setStatistics(res?.data?.list)
      setHeaderStatistics({
        total_downloads: res?.data?.total_downloads,
        total_earnings: res?.data?.total_earnings,
        total_unpaid_earnings: res?.data?.total_unpaid_earnings,
      })
      setPagination({ ...res?.data?.pagination })
      setIsLoading(false)
    } catch (error) {
      console.error('Error fetching data:', error)
      setIsLoading(false)

      Notiflix.Report.failure(
        'Error',
        'Failed to fetch data. Please try again later.',
        'OK'
      )
    }
  }

  useEffect(() => {
    getMyStatistics()
  }, [pagination?.currentPage, selectedValueMonth, date, imageIds])

  // Handle tag input - only accept numbers
  const handleImageIdsChange = (event: any, newValue: string[]) => {
    // Filter to only include valid numbers
    const validIds = newValue.filter(value => /^\d+$/.test(value.trim()))
    setImageIds(validIds)
    // Reset to first page when search changes
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }

  return (
    <div>
      <div className={styles.totalStatsContainer}>
        <span>
          <span className={styles.text}>{t('Total Downloads')}</span>
          {isLoading ? <Skeleton width={80} /> : <span className={styles.boldText}>{headerStatistics?.total_downloads}</span>}
        </span>
        <span>
          <span className={styles.text}>{t('Total Earnings')}</span>
          {isLoading ? <Skeleton width={80} /> : <span className={styles.boldText}>{headerStatistics?.total_earnings}</span>}
        </span>
        <span>
          <span className={styles.text}>{t('Unpaid Earnings')}</span>
          {isLoading ? <Skeleton width={80} /> : <span className={styles.boldText}>{headerStatistics?.total_unpaid_earnings}</span>}
        </span>
      </div>
      
      <div className={styles.filtersContainer}>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={imageIds}
          onChange={handleImageIdsChange}
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                key={index}
              />
            ))
          }
          renderInput={(params) => (
            <CustomTextfield
              {...params}
              label={t('Image IDs')}
              placeholder={t('Type ID and press Enter')}
              variant="outlined"
              size="small"
            />
          )}
          className={styles.textfield}
        />

        <CustomTextfield
          select
          label={t('Number of months')}
          variant='outlined'
          value={selectedValueMonth}
          onChange={(e: any) => setSelectedValueMonth(e.target.value)}
        >
          <MenuItem value={'all'}>{t('all_time')}</MenuItem>
          <MenuItem value={'3'}>{t('Months', { month: 3 })}</MenuItem>
          <MenuItem value={'6'}>{t('Months', { month: 6 })}</MenuItem>
          <MenuItem value={'12'}>{t('Year')}</MenuItem>
        </CustomTextfield>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            className={styles.textfield}
            label={t('Month Label')}
            value={date}
            format='MM-yyyy'
            views={['month', 'year']}
            onChange={(newValue: any) => {
              setDate(newValue)
            }}
            slots={{
              textField: CustomTextfield,
            }}
            slotProps={{
              textField: { size: 'small' },
            }}
          />
        </LocalizationProvider>
      </div>

      {!isLoading && !statistics?.length 
        ? (
          <div className={styles.totalStatsContainer}>
            <Typography>{t('no_statistics')}</Typography>
          </div>
        )
        : (
          <div className={styles.table}>
            <TableContainer component={Paper} className={styles.table}>
              <Table>
                <TableHead>
                  <TableRow className={styles.tableRow}>
                    <TableCell align='center'>{t('Image ID')}</TableCell>
                    <TableCell align='center'>{t('Upload Date')}</TableCell>
                    <TableCell align='center'>
                      {t('Total Image Downloads')}
                    </TableCell>
                    <TableCell align='center'>
                      {t('Total Image Earnings')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <>
                      {[1, 2, 3, 4, 5].map((number) => (
                        <TableRow key={number} className={styles.tableRow}>
                          <TableCell>
                            <div className={styles.imageContainer}>
                              <Skeleton variant="rectangular" width={50} height={50} />
                              <Skeleton width={100} style={{ marginLeft: 10 }} />
                            </div>
                          </TableCell>
                          <TableCell align='center'>
                            <Skeleton width={100} />
                          </TableCell>
                          <TableCell align='center'>
                            <Skeleton width={60} />
                          </TableCell>
                          <TableCell align='center'>
                            <Skeleton width={80} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <>
                      {(statistics || []).map((statistic) => (
                        <TableRow key={statistic.id} className={styles.tableRow}>
                          <TableCell>
                            <div className={styles.imageContainer}>
                              <img
                                src={statistic.thumbURL}
                                alt={`${statistic.thumbURL}`}
                                height={50}
                              />
                              <span className='text-md'>{`${t('Image ID')}: ${statistic.id}`}</span>
                            </div>
                          </TableCell>
                          <TableCell align='center'>
                            {formatDate(statistic?.upload_date || '' as any)}
                          </TableCell>
                          <TableCell align='center'>{statistic?.downloads}</TableCell>
                          <TableCell align='center'>{statistic?.earnings}</TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination?.totalPages > 1 && (
              <CustomPagination
                shape='rounded'
                count={pagination?.totalPages}
                page={pagination?.currentPage}
                onChange={handleChange}
                size='large'
                className={styles.pagination}
              />
            )}
          </div>
        )}
    </div>
  )
}